import { initializeApp } from 'firebase/app';
import { 
  getFirestore, 
  collection, 
  doc,
  getDocs, 
  addDoc, 
  updateDoc, 
  deleteDoc,
  onSnapshot,
  query,
  orderBy,
  setDoc,
  getDoc
} from 'firebase/firestore';
import { PoopReport, Settings } from '../types';

// Firebase configuration - YOU MUST REPLACE THESE VALUES
// See DEPLOYMENT_GUIDE.md for instructions
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY || "YOUR_API_KEY",
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || "YOUR_PROJECT.firebaseapp.com",
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || "YOUR_PROJECT_ID",
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || "YOUR_PROJECT.appspot.com",
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || "YOUR_SENDER_ID",
  appId: import.meta.env.VITE_FIREBASE_APP_ID || "YOUR_APP_ID"
};

// Check if Firebase is configured
export const isFirebaseConfigured = () => {
  return firebaseConfig.apiKey !== "YOUR_API_KEY" && 
         firebaseConfig.projectId !== "YOUR_PROJECT_ID";
};

// Initialize Firebase (only if configured)
let app: any = null;
let db: any = null;

if (isFirebaseConfigured()) {
  try {
    app = initializeApp(firebaseConfig);
    db = getFirestore(app);
  } catch (error) {
    console.error('Firebase initialization error:', error);
  }
}

// Collection names
const COLLECTIONS = {
  POOP_REPORTS: 'poopReports',
  SETTINGS: 'settings'
};

// Default settings
const DEFAULT_SETTINGS: Settings = {
  expiryDays: 30,
  blurMeters: 0,
  headerBgColor: '#9a3612',
  headerTextColor: '#ffffff',
  footerBgColor: '#9a3612',
  footerTextColor: '#ffffff',
  logoText: 'Pin the Poo',
  logoFont: 'The Seasons',
  logoFontSize: 1.5,
  logoColor: '#ffffff',
  tagline: 'Keeping our streets clean, one pin at a time',
  missionTitle: 'Our Mission',
  missionContent: '',
  privacyContent: '',
  termsContent: '',
  contactEmail: 'hello@pinthepoo.com',
  contactName: 'Pin the Poo Team'
};

// ============ POOP REPORTS ============

// Get all poop reports (one-time fetch)
export async function getPoopReports(): Promise<PoopReport[]> {
  if (!db) return [];
  
  try {
    const q = query(collection(db, COLLECTIONS.POOP_REPORTS), orderBy('createdAt', 'desc'));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as PoopReport));
  } catch (error) {
    console.error('Error fetching poop reports:', error);
    return [];
  }
}

// Subscribe to poop reports (real-time updates)
export function subscribeToPoopReports(callback: (reports: PoopReport[]) => void): () => void {
  if (!db) {
    callback([]);
    return () => {};
  }
  
  const q = query(collection(db, COLLECTIONS.POOP_REPORTS), orderBy('createdAt', 'desc'));
  
  const unsubscribe = onSnapshot(q, (snapshot) => {
    const reports = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as PoopReport));
    callback(reports);
  }, (error) => {
    console.error('Error subscribing to poop reports:', error);
    callback([]);
  });
  
  return unsubscribe;
}

// Add a new poop report
export async function addPoopReport(report: Omit<PoopReport, 'id'>): Promise<string | null> {
  if (!db) return null;
  
  try {
    const docRef = await addDoc(collection(db, COLLECTIONS.POOP_REPORTS), report);
    return docRef.id;
  } catch (error) {
    console.error('Error adding poop report:', error);
    return null;
  }
}

// Update a poop report
export async function updatePoopReport(id: string, updates: Partial<PoopReport>): Promise<boolean> {
  if (!db) return false;
  
  try {
    const docRef = doc(db, COLLECTIONS.POOP_REPORTS, id);
    await updateDoc(docRef, updates);
    return true;
  } catch (error) {
    console.error('Error updating poop report:', error);
    return false;
  }
}

// Delete a poop report
export async function deletePoopReport(id: string): Promise<boolean> {
  if (!db) return false;
  
  try {
    const docRef = doc(db, COLLECTIONS.POOP_REPORTS, id);
    await deleteDoc(docRef);
    return true;
  } catch (error) {
    console.error('Error deleting poop report:', error);
    return false;
  }
}

// ============ SETTINGS ============

// Get settings
export async function getSettings(): Promise<Settings> {
  if (!db) return DEFAULT_SETTINGS;
  
  try {
    const docRef = doc(db, COLLECTIONS.SETTINGS, 'global');
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return { ...DEFAULT_SETTINGS, ...docSnap.data() } as Settings;
    } else {
      // Create default settings if they don't exist
      await setDoc(docRef, DEFAULT_SETTINGS);
      return DEFAULT_SETTINGS;
    }
  } catch (error) {
    console.error('Error fetching settings:', error);
    return DEFAULT_SETTINGS;
  }
}

// Subscribe to settings (real-time updates)
export function subscribeToSettings(callback: (settings: Settings) => void): () => void {
  if (!db) {
    callback(DEFAULT_SETTINGS);
    return () => {};
  }
  
  const docRef = doc(db, COLLECTIONS.SETTINGS, 'global');
  
  const unsubscribe = onSnapshot(docRef, (docSnap) => {
    if (docSnap.exists()) {
      callback({ ...DEFAULT_SETTINGS, ...docSnap.data() } as Settings);
    } else {
      callback(DEFAULT_SETTINGS);
    }
  }, (error) => {
    console.error('Error subscribing to settings:', error);
    callback(DEFAULT_SETTINGS);
  });
  
  return unsubscribe;
}

// Update settings
export async function updateSettings(updates: Partial<Settings>): Promise<boolean> {
  if (!db) return false;
  
  try {
    const docRef = doc(db, COLLECTIONS.SETTINGS, 'global');
    const currentSettings = await getSettings();
    await setDoc(docRef, { ...currentSettings, ...updates });
    return true;
  } catch (error) {
    console.error('Error updating settings:', error);
    return false;
  }
}

export { DEFAULT_SETTINGS };
